import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { X, User, Clock, Activity, FileText, Copy, CheckCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { AuditLog } from '@/services/AuditService';

interface LogDetailsModalProps {
  log: AuditLog | null;
  open: boolean;
  onClose: () => void;
}

export const LogDetailsModal: React.FC<LogDetailsModalProps> = ({ log, open, onClose }) => {
  if (!log) return null;

  const userName = log.profiles?.name || 'Usuário do Sistema';
  const userEmail = log.profiles?.email || '';
  const userInitials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const formatDetails = (details: string) => {
    try {
      const parsed = JSON.parse(details);
      return parsed;
    } catch {
      return { raw: details };
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getActionColor = (action: string) => {
    if (action.includes('create')) return 'bg-green-100 text-green-800';
    if (action.includes('update')) return 'bg-blue-100 text-blue-800';
    if (action.includes('delete')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const details = formatDetails(log.details);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Detalhes do Log de Auditoria</span>
          </DialogTitle>
          <DialogDescription>
            Informações detalhadas sobre o evento registrado no log de auditoria.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações Básicas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Ação</label>
              <Badge className={`${getActionColor(log.action)} border-0`}>
                {log.action.replace(/_/g, ' ').toUpperCase()}
              </Badge>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Data/Hora</label>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                </span>
              </div>
            </div>
          </div>

          {/* Usuário */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Usuário</label>
            <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs">{userInitials}</AvatarFallback>
              </Avatar>
              <div>
                <div className="font-medium">{userName}</div>
                {userEmail && <div className="text-sm text-muted-foreground">{userEmail}</div>}
              </div>
            </div>
          </div>

          {/* Detalhes da Ação */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Detalhes da Ação</label>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm font-medium">Dados Alterados</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(JSON.stringify(details, null, 2))}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copiar
                </Button>
              </div>
              <pre className="text-sm whitespace-pre-wrap overflow-x-auto bg-background p-3 rounded border">
                {JSON.stringify(details, null, 2)}
              </pre>
            </div>
          </div>

          {/* Informações Técnicas */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Informações Técnicas
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">ID do Log:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">
                  {log.id}
                </div>
              </div>
              <div>
                <span className="font-medium">Profile ID:</span>
                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">
                  {log.profile_id || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Resumo da Ação */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Resumo</label>
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <div className="font-medium text-blue-900 dark:text-blue-100">
                    Ação Registrada com Sucesso
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Esta ação foi registrada automaticamente pelo sistema e está disponível para
                    auditoria e rastreamento de mudanças.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
